import React, { useCallback, useMemo } from 'react';
import { useCalendarNavigation } from '../context/CalendarNavigationContext';
import { useCalendarTimeGrid } from '../hooks/useCalendarTimeGrid';
import { useCalendarEvents } from '../hooks/useCalendarEvents';
import { useCalendar } from '../context/CalendarContext';
import { getWeekDays } from '../utils/dateHelpers';
import CalendarTimeColumn from './CalendarTimeColumn';
import CalendarEvent from './CalendarEvent';
import CalendarTimeSlot from './CalendarTimeSlot';
import CalendarCurrentTimeLine from './CalendarCurrentTimeLine';
import { CALENDAR_CONSTANTS, DAY_NAMES_PL } from '../utils/constants';

const CalendarGrid = ({
                          events = [],
                          renderEvent,
                          renderDay,
                          renderTimeSlot,
                          onEventClick,
                          onSlotClick,
                          showCurrentTime = true,
                          className = '',
                      }) => {
    const { currentWeekStart } = useCalendarNavigation();
    const { features, timeRange, hourHeight, workingHours } = useCalendar();
    const { scrollContainerRef, timeSlots } = useCalendarTimeGrid();
    const { getEventsForDayWithPositions } = useCalendarEvents({
        events,
        startHour: timeRange.startHour,
        hourHeight,
    });

    const weekDays = getWeekDays(currentWeekStart);
    const gridHeight = timeSlots.length * hourHeight;

    // Helper to check if a time slot is within working hours
    const isSlotWithinWorkingHours = useCallback((slotTime, dayDate) => {
        if (!workingHours || workingHours.length === 0) {
            return true; // If no working hours defined, allow all slots
        }

        const dayOfWeekMapping = {
            1: 'MONDAY',
            2: 'TUESDAY',
            3: 'WEDNESDAY',
            4: 'THURSDAY',
            5: 'FRIDAY',
            6: 'SATURDAY',
            7: 'SUNDAY',
        };

        const dayOfWeek = dayOfWeekMapping[dayDate.isoWeekday()];
        const dayHours = workingHours.find((wh) => wh.dayOfWeek === dayOfWeek);

        if (!dayHours) {
            return false; // No working hours for this day
        }

        const [openHour, openMin] = dayHours.openingTime.split(':').map(Number);
        const [closeHour, closeMin] = dayHours.closingTime.split(':').map(Number);
        const [slotHour, slotMin] = slotTime.split(':').map(Number);

        const slotMinutes = slotHour * 60 + (slotMin || 0);
        const openMinutes = openHour * 60 + openMin;
        const closeMinutes = closeHour * 60 + closeMin;

        return slotMinutes >= openMinutes && slotMinutes < closeMinutes;
    }, [workingHours]);

    const defaultRenderEvent = (event) => {
        const getVariant = () => {
            if (event.isOwnerConfirmed) return 'confirmed';
            if (event.isOwnerPending) return 'pending';
            if (event.bookingCount && event.bookingCount > 0) return 'pending';
            if (event.confirmed && event.dogDTO) return 'confirmed';
            if (event.trainingGroup) return 'group';
            if (event.isTemporary) return 'temporary-lock';
            if (event.isLocked) return 'locked';
            if (event.isConfirmedOther) return 'group';
            if (event.isUnavailable || event.isPast) return 'unavailable';
            return 'free';
        };

        return (
            <CalendarEvent
                key={event.id || event.slotId || `${event.startTime}-${event.endTime}`}
                event={event}
                variant={getVariant()}
                position={event.position}
                overlap={event.overlap}
                onClick={() => onEventClick?.(event)}
            />
        );
    };

    const defaultRenderTimeSlot = useCallback((slot, day) => {
        const isWithinWorkingHours = isSlotWithinWorkingHours(slot.time, day.date);
        const isSelectable = features.slotSelection && isWithinWorkingHours;

        return (
            <CalendarTimeSlot
                key={`${day.key}-${slot.time}`}
                day={day}
                time={slot.time}
                selectable={isSelectable}
                onClick={isSelectable ? onSlotClick : undefined}
                className={!isWithinWorkingHours ? 'calendar-time-slot--non-working' : ''}
            />
        );
    }, [features.slotSelection, isSlotWithinWorkingHours, onSlotClick]);

    return (
        <div className={`calendar-container ${className}`}>
            <div className="calendar-grid" ref={scrollContainerRef}>
                <div className="calendar-time-column">
                    <div className="calendar-time-column__header" />
                    {timeSlots.map((slot) => (
                        <div
                            key={slot.time}
                            className="calendar-time-column__label"
                            style={{ height: hourHeight }}
                        >
                            {slot.time}
                        </div>
                    ))}
                </div>

                {weekDays.map((day) => {
                    const dayEvents = getEventsForDayWithPositions(day.date);

                    return (
                        <div
                            key={day.key}
                            className={`calendar-day ${day.isToday ? 'calendar-day--today' : ''}`}
                        >
                            <div className="calendar-day__header">
                                <span className="calendar-day__name">{DAY_NAMES_PL[day.isoWeekday - 1]}</span>
                                <span className="calendar-day__date">{day.dayNumber}</span>
                            </div>

                            <div className="calendar-day__content" style={{ height: gridHeight }}>
                                {timeSlots.map((slot) =>
                                    renderTimeSlot
                                        ? renderTimeSlot(slot, day)
                                        : defaultRenderTimeSlot(slot, day)
                                )}

                                {dayEvents.map((event) =>
                                    renderEvent ? renderEvent(event) : defaultRenderEvent(event)
                                )}

                                {showCurrentTime && features.showCurrentTime && day.isToday && (
                                    <CalendarCurrentTimeLine />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CalendarGrid;